import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search,
  Globe2,
  Loader2,
  Eye,
  MapPin,
  User,
  FileText,
  X,
  ScanLine,
  ChevronLeft,
  ChevronRight,
  Clock,
  Compass,
} from "lucide-react";
import { toast } from "sonner";
import StreetViewModal from "./StreetViewModal";
import DetalhesModal from "./DetalhesModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESRI_IMAGERY =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const CONTAINER_ID = "cesiumContainer";

const HIST_KEY = "historico_consultas";

function carregarHistorico() {
  try {
    return JSON.parse(localStorage.getItem(HIST_KEY) || "[]");
  } catch (e) {
    return [];
  }
}

function salvarHistorico(lista) {
  try {
    localStorage.setItem(HIST_KEY, JSON.stringify(lista.slice(0, 15)));
  } catch (e) {
    /* cota cheia */
  }
}

function mascararCPF(valor) {
  const d = valor.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
}

function enderecoLinha(e) {
  if (e.endereco_corrigido) return e.endereco_corrigido;
  const partes = [e.endereco, e.numero ? `nº ${e.numero}` : "", e.complemento]
    .filter(Boolean)
    .join(", ");
  return partes || "Endereço";
}

export default function EarthExplorer() {
  const viewerRef = useRef(null);
  const markersRef = useRef([]);
  const autoSpinRef = useRef(true);
  const handlerRef = useRef(null);
  const voarRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [pessoa, setPessoa] = useState(null);
  const [enderecos, setEnderecos] = useState([]);
  const [dados, setDados] = useState(null);
  const [streetView, setStreetView] = useState(null);
  const [svLoading, setSvLoading] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [mostrarDados, setMostrarDados] = useState(false);
  const [dadosOcultos, setDadosOcultos] = useState(false);
  const [historico, setHistorico] = useState(() => carregarHistorico());
  const [showHist, setShowHist] = useState(false);
  const [selIdx, setSelIdx] = useState(-1);

  /* -------------------- Inicialização do globo -------------------- */
  // Proteção: libera a interface após alguns segundos mesmo que o globo demore
  // a carregar (evita travar na tela de carregamento em ambientes lentos).
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 12000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let destroyed = false;

    function initViewer() {
      const Cesium = window.Cesium;
      Cesium.Ion.defaultAccessToken = "";

      const viewer = new Cesium.Viewer(CONTAINER_ID, {
        baseLayer: false,
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        animation: false,
        timeline: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
      });

      viewer.imageryLayers.addImageryProvider(
        new Cesium.UrlTemplateImageryProvider({
          url: ESRI_IMAGERY,
          maximumLevel: 19,
          credit: "Imagery: Esri, Maxar, Earthstar Geographics",
        })
      );

      viewer.scene.globe.showGroundAtmosphere = true;
      viewer.scene.skyAtmosphere.show = true;
      viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#0b1426");
      viewer.scene.backgroundColor = Cesium.Color.fromCssColorString("#04070f");

      const ctrl = viewer.scene.screenSpaceCameraController;
      ctrl.minimumZoomDistance = 600;
      ctrl.maximumZoomDistance = 24000000;

      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(-20, 12, 24000000),
      });

      let lastMs = performance.now();
      viewer.scene.preRender.addEventListener(() => {
        const now = performance.now();
        const dt = (now - lastMs) / 1000;
        lastMs = now;
        if (!autoSpinRef.current) return;
        viewer.scene.camera.rotate(Cesium.Cartesian3.UNIT_Z, -0.045 * dt);
      });

      const stopSpin = () => {
        autoSpinRef.current = false;
      };
      viewer.scene.canvas.addEventListener("pointerdown", stopSpin);
      viewer.scene.canvas.addEventListener("wheel", stopSpin);

      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((movement) => {
        const picked = viewer.scene.pick(movement.position);
        if (Cesium.defined(picked) && picked.id) {
          const item = markersRef.current.find((m) => m.entity === picked.id);
          if (item && voarRef.current) {
            voarRef.current(item.endereco);
          }
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      handler.setInputAction((movement) => {
        const picked = viewer.scene.pick(movement.endPosition);
        const sobre =
          Cesium.defined(picked) &&
          picked.id &&
          markersRef.current.some((m) => m.entity === picked.id);
        viewer.scene.canvas.style.cursor = sobre ? "pointer" : "";
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      handlerRef.current = handler;
      return viewer;
    }

    const interval = setInterval(() => {
      if (window.Cesium && window.Cesium.Viewer && document.getElementById(CONTAINER_ID)) {
        clearInterval(interval);
        if (destroyed) return;
        try {
          viewerRef.current = initViewer();
          setReady(true);
        } catch (e) {
          console.error("Falha ao iniciar o globo", e);
        }
      }
    }, 120);

    return () => {
      destroyed = true;
      clearInterval(interval);
      if (handlerRef.current && !handlerRef.current.isDestroyed()) {
        handlerRef.current.destroy();
      }
      handlerRef.current = null;
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
      }
      viewerRef.current = null;
    };
  }, []);

  /* -------------------- Câmera -------------------- */
  const voarPara = useCallback((lat, lon, aoChegar) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;
    if (!isFinite(lat) || !isFinite(lon)) return;
    autoSpinRef.current = false;
    const centro = Cesium.Cartesian3.fromDegrees(lon, lat);
    const esfera = new Cesium.BoundingSphere(centro, 1200);
    viewer.camera.flyToBoundingSphere(esfera, {
      duration: 2.6,
      offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-65), 900),
      complete: () => {
        if (typeof aoChegar === "function") aoChegar();
      },
    });
  }, []);

  const desenharMarcadores = useCallback((lista) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;

    markersRef.current.forEach((m) => viewer.entities.remove(m.entity));
    markersRef.current = [];

    lista.forEach((e, i) => {
      const lat = parseFloat(e.latitude);
      const lon = parseFloat(e.longitude);
      if (!isFinite(lat) || !isFinite(lon) || (lat === 0 && lon === 0)) return;
      const pos = Cesium.Cartesian3.fromDegrees(lon, lat);
      const ent = viewer.entities.add({
        position: pos,
        point: {
          pixelSize: 14,
          color: Cesium.Color.fromCssColorString("#2dd4bf"),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        label: {
          text: String(i + 1),
          font: "700 12px Manrope, sans-serif",
          fillColor: Cesium.Color.fromCssColorString("#04070f"),
          showBackground: false,
          pixelOffset: new Cesium.Cartesian2(0, 0),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        },
      });
      markersRef.current.push({ entity: ent, lat, lon, indice: i, endereco: e });
    });
  }, []);

  /* -------------------- Busca por CPF -------------------- */
  const buscarCPF = async (e) => {
    e.preventDefault();
    const doc = cpf.replace(/\D/g, "");
    if (doc.length !== 11) {
      toast.error("Digite um CPF válido (11 dígitos).");
      return;
    }
    setLoading(true);
    setMostrarDados(false);
    try {
      const { data } = await axios.post(`${API}/cpf`, { cpf: doc }, { timeout: 120000 });
      const d = data.dados || {};
      const cad = d.cadastral || {};
      const foto = Array.isArray(d.fotos) && d.fotos[0] ? d.fotos[0].foto : null;
      if (!cad.nome && (!d.enderecos || d.enderecos.length === 0)) {
        toast.error("Nenhum dado encontrado para esse CPF.");
        return;
      }
      setPessoa({ ...cad, foto });
      const ends = Array.isArray(d.enderecos) ? d.enderecos : [];
      setEnderecos(ends);
      setDados(d);
      desenharMarcadores(ends);
      // Salva no histórico (cache do navegador), sem duplicar o mesmo CPF.
      const entrada = { cpf: doc, nome: cad.nome || doc, foto: foto || null, dados: d, pessoa: { ...cad, foto }, enderecos: ends };
      setHistorico((prev) => [entrada, ...prev.filter((h) => h.cpf !== doc)].slice(0, 15));
      // Vai automaticamente ao 1º endereço e abre o Street View 360 ao chegar.
      const primeiro = ends.find((e) => {
        const la = parseFloat(e.latitude);
        const lo = parseFloat(e.longitude);
        return isFinite(la) && isFinite(lo) && !(la === 0 && lo === 0);
      });
      if (primeiro) {
        voarPara(parseFloat(primeiro.latitude), parseFloat(primeiro.longitude), () =>
          abrirStreetView(primeiro)
        );
      }
    } catch (err) {
      toast.error("Não foi possível consultar o CPF. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Street View do endereço -------------------- */
  const resetGlobo = useCallback(() => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;
    autoSpinRef.current = true;
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(-20, 12, 24000000),
      duration: 2.0,
    });
  }, []);

  const abrirStreetView = useCallback(async (endereco) => {
    setMostrarDados(true);
    const label = `${enderecoLinha(endereco)} · ${endereco.bairro || endereco.cidade || ""}`;
    if (endereco.pano_id) {
      setStreetView({
        pano_id: endereco.pano_id,
        facade_heading: endereco.facade_heading ?? 0,
        label,
      });
      return;
    }
    const lat = parseFloat(endereco.latitude);
    const lon = parseFloat(endereco.longitude);
    setSvLoading(true);
    try {
      const { data } = await axios.get(`${API}/streetview/find`, {
        params: { lat, lng: lon },
      });
      if (!data.found || !data.pano_id) {
        toast.error("Sem Street View neste endereço.");
        return;
      }
      setStreetView({
        pano_id: data.pano_id,
        facade_heading: data.facade_heading ?? 0,
        label,
      });
    } catch (e2) {
      toast.error("Não foi possível abrir o Street View.");
    } finally {
      setSvLoading(false);
    }
  }, []);

  // Clicar no endereço: fecha o 360, voa até o ponto e abre o 360 ao chegar.
  const irParaEndereco = useCallback(
    (endereco, idx) => {
      if (idx === selIdx && streetView) return;
      setSelIdx(idx);
      setStreetView(null);
      voarPara(
        parseFloat(endereco.latitude),
        parseFloat(endereco.longitude),
        () => abrirStreetView(endereco)
      );
    },
    [voarPara, abrirStreetView, selIdx, streetView]
  );

  useEffect(() => {
    voarRef.current = irParaEndereco;
  }, [irParaEndereco]);

  const imgProxy = (url) => `${API}/img?u=${encodeURIComponent(url)}`;

  const limpar = useCallback(() => {
    setStreetView(null);
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      markersRef.current.forEach((m) => viewer.entities.remove(m.entity));
      markersRef.current = [];
      const Cesium = window.Cesium;
      autoSpinRef.current = false;
      // Reverso do voo: sobe de volta para a visão do globo.
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(-20, 12, 24000000),
        duration: 2.2,
      });
    }
    setPessoa(null);
    setEnderecos([]);
    setDados(null);
    setShowDetalhes(false);
    setMostrarDados(false);
    setSelIdx(-1);
    setDadosOcultos(false);
  }, []);

  const restaurarEntrada = useCallback(
    (h) => {
      if (!h) return;
      setShowHist(false);
      setStreetView(null);
      setPessoa(h.pessoa);
      setEnderecos(h.enderecos);
      setDados(h.dados);
      setCpf(mascararCPF(h.cpf));
      desenharMarcadores(h.enderecos || []);
      const prim = (h.enderecos || []).find((e) => {
        const la = parseFloat(e.latitude);
        const lo = parseFloat(e.longitude);
        return isFinite(la) && isFinite(lo) && !(la === 0 && lo === 0);
      });
      if (prim)
        voarPara(
          parseFloat(prim.latitude),
          parseFloat(prim.longitude),
          () => abrirStreetView(prim)
        );
    },
    [desenharMarcadores, voarPara, abrirStreetView]
  );

  const thumbUrl = (e, w, h) =>
    `${API}/streetview/thumbnail?pano_id=${encodeURIComponent(e.pano_id)}&heading=${
      e.facade_heading ?? 0
    }&w=${w}&h=${h}`;

  /* -------------------- UI -------------------- */
  return (
    <div className="relative h-screen w-screen overflow-hidden" data-testid="earth-explorer">
      <div id={CONTAINER_ID} />

      {/* Loading do globo */}
      <AnimatePresence>
        {!ready && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#04070f]"
            data-testid="globe-loading"
          >
            <Globe2 className="h-14 w-14 text-teal-300 spin-slow" strokeWidth={1.4} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gradiente superior */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-black/70 to-transparent" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-10 h-56 bg-gradient-to-t from-black/80 to-transparent" />

      {/* Card da pessoa (topo direito / topo no mobile) */}
      <AnimatePresence>
        {pessoa && mostrarDados && !dadosOcultos && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="glass fixed left-3 right-3 top-3 z-[62] overflow-hidden rounded-2xl shadow-2xl shadow-black/50 sm:left-auto sm:right-6 sm:top-6 sm:w-80 sm:max-w-[calc(100vw-2rem)]"
            data-testid="pessoa-card"
          >
            <div className="flex items-start gap-3 p-4">
              {pessoa.foto ? (
                <img
                  src={imgProxy(pessoa.foto)}
                  alt={pessoa.nome || "Foto"}
                  className="h-20 w-20 shrink-0 rounded-xl object-cover ring-2 ring-teal-400/50"
                  onError={(ev) => {
                    ev.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl bg-white/10">
                  <User className="h-9 w-9 text-slate-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-bold text-white" data-testid="pessoa-nome">
                  {pessoa.nome || "Sem nome"}
                </p>
                {pessoa.cpfMask && (
                  <p className="mt-0.5 font-mono text-xs text-teal-300/90">{pessoa.cpfMask}</p>
                )}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {pessoa.sexo && <Chip>{pessoa.sexo === "M" ? "Masculino" : pessoa.sexo === "F" ? "Feminino" : pessoa.sexo}</Chip>}
                  {pessoa.idade ? <Chip>{pessoa.idade} anos</Chip> : null}
                  {pessoa.dataNasc && <Chip>{pessoa.dataNasc}</Chip>}
                </div>
              </div>
              <button
                onClick={limpar}
                className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Limpar"
                data-testid="limpar-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-1 px-4 pb-2 text-xs text-slate-300">
              {pessoa.mae?.nome && <LinhaInfo rotulo="Mãe" valor={pessoa.mae.nome} />}
              {pessoa.naturalidade && <LinhaInfo rotulo="Natural de" valor={pessoa.naturalidade} />}
              {pessoa.renda && <LinhaInfo rotulo="Renda" valor={pessoa.renda} />}
              {pessoa.escolaridade && <LinhaInfo rotulo="Escolaridade" valor={pessoa.escolaridade} />}
            </div>

            <button
              onClick={() => setShowDetalhes(true)}
              className="m-3 mt-2 flex w-[calc(100%-1.5rem)] items-center justify-center gap-2 rounded-xl bg-teal-400 px-3 py-2 text-sm font-semibold text-slate-900 transition-all hover:bg-teal-300"
              data-testid="ver-detalhado-btn"
            >
              <FileText className="h-4 w-4" /> Ver detalhado
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de endereços (esquerda no desktop / folha inferior no mobile) */}
      <AnimatePresence>
        {enderecos.length > 0 && mostrarDados && !dadosOcultos && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="fixed inset-x-3 bottom-28 z-[65] flex max-h-[42vh] flex-col sm:inset-x-auto sm:left-6 sm:top-6 sm:bottom-auto sm:max-h-[calc(100vh-2rem)] sm:w-[22rem]"
            data-testid="enderecos-lista"
          >
            <div className="glass mb-2 flex items-center justify-between gap-2 rounded-xl px-3 py-2 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="grid h-7 w-7 place-items-center rounded-lg bg-teal-400/20 ring-1 ring-teal-400/30">
                  <MapPin className="h-4 w-4 text-teal-300" />
                </div>
                <div>
                  <p className="text-sm font-bold leading-none text-white">
                    {enderecos.length} endereço{enderecos.length > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
              {enderecos.map((e, i) => (
                <button
                  key={i}
                  onClick={() => irParaEndereco(e, i)}
                  className={`glass group flex w-full items-center gap-3 rounded-2xl p-2.5 text-left shadow-lg shadow-black/40 transition-all hover:scale-[1.015] hover:ring-1 hover:ring-teal-400/40 ${selIdx === i ? "ring-2 ring-teal-400" : ""}`}
                  data-testid="endereco-card"
                >
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl ring-1 ring-white/10">
                    {e.pano_id ? (
                      <img
                        src={thumbUrl(e, 200, 200)}
                        alt="Fachada"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center bg-gradient-to-br from-white/10 to-white/[0.02]">
                        <MapPin className="h-7 w-7 text-slate-600" />
                      </div>
                    )}
                    <span className="absolute left-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-teal-400 text-[10px] font-bold text-slate-900 shadow-md shadow-teal-500/40">
                      {i + 1}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-semibold leading-snug text-white">
                      {enderecoLinha(e)}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {[e.bairro, e.cidade && `${e.cidade}${e.uf ? `/${e.uf}` : ""}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {e.cep && (
                        <span className="rounded-md bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-slate-300 ring-1 ring-white/10">
                          {e.cep}
                        </span>
                      )}
                      {e.classificacao && (
                        <span className="rounded-md bg-teal-400/10 px-1.5 py-0.5 text-[10px] font-semibold text-teal-200 ring-1 ring-teal-400/20">
                          Classe {e.classificacao}
                        </span>
                      )}
                      {e.pano_id && (
                        <span className="rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200 ring-1 ring-emerald-500/20">
                          360°
                        </span>
                      )}
                    </div>
                  </div>
                  <Eye className="h-4 w-4 shrink-0 text-teal-300 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Realinhar / centralizar o globo — só na tela inicial */}
      {!pessoa && (
        <button
          onClick={resetGlobo}
          className="glass fixed right-3 top-3 z-[67] grid h-11 w-11 place-items-center rounded-2xl text-teal-200 shadow-2xl shadow-black/50 transition-transform hover:scale-105"
          data-testid="reset-globo-btn"
          title="Centralizar o mapa"
        >
          <Compass className="h-5 w-5" />
        </button>
      )}

      {/* Histórico de consultas (cache) — só na tela inicial */}
      {historico.length > 0 && !pessoa && (
        <button
          onClick={() => setShowHist((v) => !v)}
          className="glass fixed left-3 top-3 z-[67] grid h-11 w-11 place-items-center rounded-2xl text-teal-200 shadow-2xl shadow-black/50 transition-transform hover:scale-105 sm:left-3 sm:top-3"
          data-testid="historico-btn"
          title="Histórico de consultas"
        >
          <Clock className="h-5 w-5" />
        </button>
      )}
      <AnimatePresence>
        {showHist && (
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className="glass fixed left-3 top-16 z-[67] flex max-h-[70vh] w-72 max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl shadow-2xl shadow-black/50"
            data-testid="historico-panel"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
              <span className="flex items-center gap-2 text-sm font-semibold text-white">
                <Clock className="h-4 w-4 text-teal-300" /> Histórico
              </span>
              <button onClick={() => setShowHist(false)} className="rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col gap-1 overflow-y-auto p-2">
              {historico.map((h) => (
                <button
                  key={h.cpf}
                  onClick={() => restaurarEntrada(h)}
                  className="flex items-center gap-2.5 rounded-xl p-2 text-left transition-colors hover:bg-white/8"
                >
                  {h.foto ? (
                    <img src={imgProxy(h.foto)} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/10" />
                  ) : (
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-white/10"><User className="h-4 w-4 text-slate-300" /></span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-white">{h.nome}</span>
                    <span className="block truncate font-mono text-[11px] text-teal-300/80">{mascararCPF(h.cpf)}</span>
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => { salvarHistorico([]); setHistorico([]); setShowHist(false); }}
              className="border-t border-white/10 px-3 py-2 text-xs text-rose-300 hover:bg-white/5"
            >
              Limpar histórico
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botão minimizar / mostrar os dados (mapa + 360 livres) */}
      {mostrarDados && (
        <button
          onClick={() => setDadosOcultos((v) => !v)}
          className="glass fixed left-3 top-3 z-[66] flex items-center gap-2 rounded-full p-1.5 pr-3 shadow-2xl shadow-black/50 transition-transform hover:scale-105 sm:left-auto sm:right-4 sm:top-auto sm:bottom-4"
          data-testid="toggle-dados"
          title={dadosOcultos ? "Mostrar dados" : "Ocultar dados"}
        >
          {pessoa?.foto ? (
            <img
              src={imgProxy(pessoa.foto)}
              alt=""
              className="h-8 w-8 rounded-full object-cover ring-2 ring-teal-400/60"
            />
          ) : (
            <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10">
              <User className="h-4 w-4 text-slate-300" />
            </span>
          )}
          {dadosOcultos ? (
            <ChevronLeft className="h-4 w-4 text-teal-200" />
          ) : (
            <ChevronRight className="h-4 w-4 text-teal-200" />
          )}
        </button>
      )}

      {/* Busca por CPF (centro inferior) */}
      <div className="fixed inset-x-0 bottom-0 z-20 flex flex-col items-center gap-2.5 px-4 pb-6">
        <p className="animate-pulse-slow bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-center font-display text-xl font-extrabold tracking-tight text-transparent drop-shadow-[0_2px_12px_rgba(45,212,191,0.35)] sm:text-2xl">
          {pessoa ? "Buscar outro CPF" : "Encontre qualquer pessoa no mapa do mundo"}
        </p>
        <form
          onSubmit={buscarCPF}
          className="glass flex w-full max-w-md items-center gap-2 rounded-2xl px-3 py-2 shadow-2xl shadow-black/50"
          data-testid="cpf-form"
        >
          <ScanLine className="ml-1 h-5 w-5 shrink-0 text-teal-300" />
          <input
            value={cpf}
            onChange={(ev) => setCpf(mascararCPF(ev.target.value))}
            placeholder="Digite o CPF (somente números)"
            inputMode="numeric"
            maxLength={14}
            className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
            data-testid="cpf-input"
          />
          <button
            type="submit"
            disabled={loading || cpf.replace(/\D/g, "").length !== 11}
            className="flex shrink-0 items-center gap-1.5 rounded-xl bg-teal-400 px-4 py-1.5 text-sm font-semibold text-slate-900 transition-all hover:bg-teal-300 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="cpf-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 spin-slow" /> : <Search className="h-4 w-4" />}
            <span className="hidden sm:inline">{loading ? "Buscando" : "Buscar"}</span>
          </button>
        </form>
      </div>

      {/* Modais */}
      <AnimatePresence>
        {showDetalhes && dados && (
          <DetalhesModal
            dados={dados}
            api={API}
            foto={pessoa?.foto}
            nome={pessoa?.nome}
            onClose={() => setShowDetalhes(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {streetView && (
          <StreetViewModal
            api={API}
            pano={streetView}
            thumbnail={
              streetView.pano_id
                ? `${API}/streetview/thumbnail?pano_id=${encodeURIComponent(
                    streetView.pano_id
                  )}&heading=${streetView.facade_heading ?? 0}&w=1024&h=576&fov=100`
                : null
            }
            onClose={() => setStreetView(null)}
          />
        )}
      </AnimatePresence>

      {svLoading && !streetView && (
        <div className="fixed inset-0 z-[55] grid place-items-center bg-black/40">
          <Loader2 className="h-10 w-10 spin-slow text-teal-300" />
        </div>
      )}
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-slate-200">
      {children}
    </span>
  );
}

function LinhaInfo({ rotulo, valor }) {
  return (
    <p className="flex gap-1.5">
      <span className="shrink-0 text-slate-500">{rotulo}:</span>
      <span className="min-w-0 truncate text-slate-200">{valor}</span>
    </p>
  );
}
